import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { Sparkles, Check, X, Download, Loader2, ArrowUpRight, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";

export default function SubscriptionPortal() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-ES" : "it-IT";
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([api.get("/subscription/status"), api.get("/subscription/history")]);
      setStatus(s.data); setHistory(h.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  async function cancelSub() {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(t("subPortal.cancelled"));
      setStatus((s) => ({ ...s, ...data }));
      setConfirmingCancel(false);
      const me = await api.get("/auth/me"); setUser(me.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }
  async function reactivateSub() {
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/reactivate");
      toast.success(t("subPortal.reactivated"));
      setStatus((s) => ({ ...s, ...data }));
      const me = await api.get("/auth/me"); setUser(me.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  }

  function downloadReceipt(tx) {
    const doc = new jsPDF();
    doc.setFontSize(22); doc.text("AMBRA — Ricevuta / Recibo", 20, 25);
    doc.setFontSize(10);
    doc.text(`Nº: ${tx.session_id?.slice(0,24) || tx.id}`, 150, 25);
    doc.text(new Date(tx.created_at).toLocaleDateString(locale), 150, 32);
    doc.setFontSize(11);
    doc.text(t("subPortal.receiptBillTo"), 20, 50);
    doc.text(user.name || user.email, 20, 57);
    doc.text(user.email, 20, 63);
    let y = 90;
    doc.setFontSize(9);
    doc.text(t("subPortal.receiptDesc"), 20, y);
    doc.text(t("subPortal.receiptAmount"), 150, y);
    doc.line(20, y+2, 195, y+2); y += 10;
    doc.text(`Ambra Pro — ${tx.plan === "yearly" ? t("pricing.yearlyTitle") : t("pricing.monthlyTitle")} (${tx.days_purchased} ${t("subPortal.days")})`, 20, y);
    doc.text(`€${Number(tx.amount).toFixed(2)}`, 150, y);
    y += 8; doc.line(130, y, 195, y); y += 6;
    doc.setFontSize(12); doc.text("TOTAL:", 130, y);
    doc.text(`€${Number(tx.amount).toFixed(2)}`, 150, y);
    y += 20; doc.setFontSize(9); doc.setTextColor(120,113,108);
    doc.text(t("subPortal.receiptFooter"), 20, y);
    doc.save(`ambra-recibo-${tx.session_id?.slice(0,12) || tx.id}.pdf`);
  }

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#D4A373]" /></div>;

  const isCancelled = user?.subscription?.status === "cancelled" || status?.status === "cancelled";
  const isLifetime = status?.status === "lifetime";
  const paidUntil = status?.paid_until ? new Date(status.paid_until) : null;
  const trialEndsAt = status?.trial_ends_at ? new Date(status.trial_ends_at) : null;
  const paidHistory = history.filter((h) => h.payment_status === "paid");

  return (
    <div className="space-y-8 fade-in-up" data-testid="subscription-portal">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("subPortal.eyebrow")}</p>
          <h1 className="font-heading text-5xl font-light">{t("subPortal.title")}</h1>
        </div>
        {!isLifetime && (
          <button onClick={() => navigate("/precios")} data-testid="portal-goto-pricing"
            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#D4A373] text-stone-900 hover:bg-[#B07D4F] hover:text-white transition-colors font-medium">
            <Sparkles className="w-4 h-4" /> {t("subPortal.changePlan")}
          </button>
        )}
      </div>

      {/* Status hero */}
      <div className={`rounded-2xl p-8 border ${isLifetime ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-white border-stone-200"}`}>
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <p className={`text-xs uppercase tracking-[0.3em] mb-2 ${isLifetime ? "text-stone-400" : "text-stone-500"}`}>{t("subPortal.currentStatus")}</p>
            <div className="flex items-center gap-2">
              <span className="font-heading text-3xl font-light capitalize">{t(`subPortal.status_${status?.status || "trial"}`)}</span>
            </div>
            {status?.status === "trial" && <p className={`text-sm mt-2 ${isLifetime ? "text-stone-400" : "text-stone-500"}`}>{t("subPortal.daysLeft", { days: status?.days_left })}</p>}
            {status?.status === "active" && status?.days_left != null && <p className={`text-sm mt-2 ${isLifetime ? "text-stone-400" : "text-stone-500"}`}>{t("subPortal.renewsIn", { days: status.days_left })}</p>}
            {isCancelled && <p className="text-sm mt-2 text-stone-500">{t("subPortal.cancelledHint")}</p>}
          </div>
          <div>
            <p className={`text-xs uppercase tracking-[0.3em] mb-2 ${isLifetime ? "text-stone-400" : "text-stone-500"}`}>{t("subPortal.paidUntil")}</p>
            <p className="font-heading text-xl font-light">
              {paidUntil ? paidUntil.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" }) : (trialEndsAt ? trialEndsAt.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" }) : "—")}
            </p>
          </div>
          <div>
            <p className={`text-xs uppercase tracking-[0.3em] mb-2 ${isLifetime ? "text-stone-400" : "text-stone-500"}`}>{t("subPortal.lastPlan")}</p>
            <p className="font-heading text-xl font-light">{isLifetime ? "Ambra Lifetime" : (paidHistory[0]?.plan === "yearly" ? t("pricing.yearlyTitle") : paidHistory[0]?.plan === "monthly" ? t("pricing.monthlyTitle") : "—")}</p>
          </div>
        </div>

        {!isLifetime && !isCancelled && paidHistory.length > 0 && !confirmingCancel && (
          <button onClick={() => setConfirmingCancel(true)} data-testid="cancel-btn"
            className="mt-6 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-red-600 flex items-center gap-1">
            <X className="w-3 h-3" /> {t("subPortal.cancelSubscription")}
          </button>
        )}
        {confirmingCancel && (
          <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-900 mb-3">{t("subPortal.cancelConfirm", { date: paidUntil?.toLocaleDateString(locale) })}</p>
              <div className="flex gap-3">
                <button onClick={cancelSub} disabled={busy} data-testid="confirm-cancel-btn"
                  className="px-4 py-1.5 rounded-full bg-red-600 text-white text-xs uppercase tracking-widest hover:bg-red-700 disabled:opacity-50">
                  {busy ? t("subPortal.cancelling") : t("subPortal.confirmCancel")}
                </button>
                <button onClick={() => setConfirmingCancel(false)} className="px-4 py-1.5 rounded-full border border-stone-300 text-xs uppercase tracking-widest hover:bg-white">{t("common.cancel")}</button>
              </div>
            </div>
          </div>
        )}
        {isCancelled && (
          <button onClick={reactivateSub} disabled={busy} data-testid="reactivate-btn"
            className="mt-6 px-5 py-2 rounded-full bg-[#8FA08B] text-white text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
            <Check className="w-3 h-3 inline mr-1" /> {t("subPortal.reactivate")}
          </button>
        )}
      </div>

      {/* Payment history */}
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
          <h3 className="font-heading text-2xl font-light">{t("subPortal.paymentHistory")}</h3>
          <span className="text-xs uppercase tracking-widest text-stone-500">{paidHistory.length} {t("subPortal.payments")}</span>
        </div>
        {paidHistory.length === 0 ? (
          <div className="py-16 text-center text-stone-500 text-sm">{t("subPortal.noPayments")}</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-[0.15em] text-stone-500 border-b border-stone-100">
                <th className="text-left px-6 py-3">{t("common.date")}</th>
                <th className="text-left px-6 py-3">{t("subPortal.colPlan")}</th>
                <th className="text-right px-6 py-3">{t("subPortal.colAmount")}</th>
                <th className="text-center px-6 py-3">{t("subPortal.colStatus")}</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {paidHistory.map((h) => (
                <tr key={h.id} data-testid={`pay-row-${h.id}`} className="border-b border-stone-50">
                  <td className="px-6 py-4 text-sm text-stone-600">{new Date(h.created_at).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" })}</td>
                  <td className="px-6 py-4 text-sm">{h.plan === "yearly" ? t("pricing.yearlyTitle") : t("pricing.monthlyTitle")}</td>
                  <td className="px-6 py-4 text-right font-medium">€{Number(h.amount).toFixed(2)}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#8FA08B] text-white">{t("subPortal.paid")}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button onClick={() => downloadReceipt(h)} data-testid={`download-receipt-${h.id}`}
                      className="text-xs uppercase tracking-[0.2em] text-[#D4A373] hover:text-[#B07D4F]">
                      <Download className="w-3.5 h-3.5 inline mr-1" /> {t("subPortal.receipt")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
